import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { CalculatedMutationOperationPairsType } from '@swissfex/sfx-commons';
import { isToday } from 'date-fns';
import { Operation } from 'fast-json-patch/module/core';

import { CANCELLED_REQUEST_DUE_TO_INCOMPLETE_DATA, CANCELLED_REQUEST_ERROR, SafeAny } from '../../constants';

export interface IFormOperationOptions {
    onlySelf?: boolean;
    emitEvent?: boolean;
}

export function nameOfDirectProperty<T>(func: (context: T) => SafeAny, context: T, isGetter = false): string {
    const parsedFunc = func
        .toString()
        .split('=>')
        .map((item) => item.trim());

    if (parsedFunc.length !== 2) {
        throw new Error(`Invalid lambda function: '${func.toString()}'`);
    }

    const returnValue = stripAssertionOperators(parsedFunc[1]).split('.');

    let supposedPropertyValue = context;
    for (let i = 1; i < returnValue.length; i++) {
        if (!supposedPropertyValue) {
            throw new Error(`Invalid expression for property: '${returnValue}'`);
        }

        supposedPropertyValue = supposedPropertyValue[returnValue[i]];
    }

    if (func(context) === supposedPropertyValue || isGetter) {
        return returnValue[returnValue.length - 1];
    }

    throw new Error(`Could not find matching property with correct value for lambda function '${func}'`);
}

export function pathOfProperty<T>(func: (context: T) => SafeAny): string {
    const parsedFunc = func
        .toString()
        .split('=>')
        .map((item) => item.trim());

    if (parsedFunc.length !== 2) {
        throw new Error(`Invalid lambda function: '${func.toString()}'`);
    }

    const returnValue = stripAssertionOperators(parsedFunc[1]).split('.');
    if (returnValue.length < 2) {
        throw new Error(`Invalid expression for property: '${returnValue}'`);
    }

    return `/${returnValue.slice(1).join('/')}`;
}

function stripAssertionOperators(value: string): string {
    return value?.replace(/[?!]/g, '');
}

export function getMutationObject(operations: Operation[]): SafeAny {
    const object = {};

    operations.forEach((operation) => {
        const keys = operation.path.split('/').filter((k) => k !== '');
        let temp = object;
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (!temp[key]) {
                temp[key] = {};
            }
            if (i === keys.length - 1) {
                temp[key] = 'value' in operation ? operation.value : null;
            } else {
                temp = temp[key];
            }
        }
    });

    return !Object.keys(object).length ? object : transformNumericKeyedObjectsToArrays(object);
}

function transformNumericKeyedObjectsToArrays(object: SafeAny): SafeAny {
    if (Array.isArray(object)) {
        return object.map(transformNumericKeyedObjectsToArrays);
    } else if (typeof object === 'object' && object !== null) {
        if (Object.keys(object).every((k) => !isNaN(Number(k)))) {
            const maxIndex = Math.max(...Object.keys(object).map(Number));
            const array = Array.from(
                { length: maxIndex + 1 },
                (_, index) => transformNumericKeyedObjectsToArrays(object[index] || null) // [null, value, null, value] is completely fine object when patching form arrays, meaning that Angular will only patch values on 1st and 3rd indices. Indices with `null` value will be ignored.
            );
            return array;
        }

        return Object.fromEntries(
            Object.entries(object).map(([key, value]) => [key, transformNumericKeyedObjectsToArrays(value)])
        );
    }

    return object;
}

export function unregisterSfxStatusChanges(abstractControl: AbstractControl): void {
    if (abstractControl) {
        abstractControl.unregisterSfxStatusChanges();
    }
}

export function wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isAccessForbiddenError(error: SafeAny): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    return !!error && error instanceof HttpErrorResponse && error.status === HttpStatusCode.Forbidden;
}

export function isCancelledRequestError(error: SafeAny): boolean {
    return !!error && (error === CANCELLED_REQUEST_ERROR || error === CANCELLED_REQUEST_DUE_TO_INCOMPLETE_DATA);
}

export function getHttpStatusCodeFromError(error: SafeAny): HttpStatusCode {
    if (error instanceof HttpErrorResponse) {
        return error.status;
    }

    return null;
}

export function patchAffectedControls<TFormGroup extends { [K in keyof TFormGroup]: AbstractControl }, TFormModel>(
    formGroup: FormGroup<TFormGroup>,
    formGroupModel: TFormModel,
    formCreationFn?: () => FormGroup<TFormGroup>,
    options?: IFormOperationOptions
): void {
    if (!formGroup || !formGroupModel) {
        return;
    }

    const mutations = formGroup.calculateMutations(formGroupModel, false, false, true);
    const hasAddOrRemoveMutations = !!mutations.find((m) => m.op === 'add' || m.op === 'remove');

    if (hasAddOrRemoveMutations && formCreationFn) {
        formCreationFn();
        return;
    }

    const replaceMutations = mutations.filter((m) => m.op === 'replace');
    if (replaceMutations.length) {
        const mutationObject = getMutationObject(replaceMutations);
        if (mutationObject && Object.keys(mutationObject).length) {
            formGroup.patchValue(mutationObject, options);
        }
    }
}

export function markControlsAsPristine(mutationOperationPairs: CalculatedMutationOperationPairsType): void {
    if (mutationOperationPairs?.length) {
        mutationOperationPairs.forEach(({ control }) => {
            if (control) {
                control.markAsPristine();
            }
        });
    }
}

export function getDisabledControlPaths(form: FormGroup | FormArray, path = ''): string[] {
    if (!form?.controls) {
        return [];
    }

    let disabledControlPaths: string[] = [];

    Object.keys(form.controls).forEach((key) => {
        const controlPath = path ? `${path}.${key}` : key;
        const control = form.get(key);

        if (control?.disabled) {
            disabledControlPaths.push(controlPath);
        }

        if (control instanceof FormGroup || control instanceof FormArray) {
            disabledControlPaths = disabledControlPaths.concat(getDisabledControlPaths(control, controlPath));
        }
    });

    return disabledControlPaths;
}

export function disableControlsByPaths(
    disabledControlPaths: string[],
    form: FormGroup | FormArray,
    options?: IFormOperationOptions
): void {
    if (!disabledControlPaths?.length || !form) {
        return;
    }

    disabledControlPaths.forEach((path) => {
        const control = form.get(path);
        if (control?.enabled) {
            control.disable(options);
        }
    });
}

export function setControlDisabledOrEnabledState(
    control: AbstractControl,
    shouldDisable: boolean,
    options?: IFormOperationOptions
): void {
    if (!control) {
        return;
    }

    const isStateChangeNeeded = shouldDisable ? control.enabled : control.disabled;
    if (isStateChangeNeeded) {
        if (shouldDisable) {
            control.disable(options);
        } else {
            control.enable(options);
        }
    }
}

export function toModelPath(mutationPath: string): string {
    return mutationPath.replaceAll('/', '.').substring(1);
}

export function getItemsWithIndexAsId<T extends object>(array: T[]): (T & { id: number })[] {
    return array?.map((item, index) => ({ ...item, id: index }));
}

export function isDateToday(date: string | Date): boolean {
    if (!date) {
        return false;
    }

    try {
        return isToday(new Date(date));
    } catch {
        return false;
    }
}
